// components/models/Mai25FlashControlPanel.tsx
import React, { memo, useCallback } from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import { Mai25FlashParams } from '#types/types';
import { useKernel } from '@/kernel/Kernel';

interface Mai25FlashControlPanelProps {
  params: Mai25FlashParams;
  setModelParams: (
    updater: (
      prevParams: { [model: string]: Mai25FlashParams }
    ) => { [model: string]: Mai25FlashParams }
  ) => void;
  diffusionModel: string;
}

export const getDefaultMai25FlashParams = (): Mai25FlashParams => ({
  prompt: '',
  seed: -1,
});

const Mai25FlashControlPanel: React.FC<Mai25FlashControlPanelProps> = ({
  params,
  setModelParams,
  diffusionModel,
}) => {
  const { kernel } = useKernel();

  const updateParam = useCallback((
    name: keyof Mai25FlashParams,
    value: Mai25FlashParams[keyof Mai25FlashParams],
  ) => {
    setModelParams((prevModelParams) => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Mai25FlashControlPanel] Missing params for model "${diffusionModel}".`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: value,
        },
      };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value: name === 'prompt' ? undefined : value,
      value_length: name === 'prompt' ? String(value || '').length : undefined,
      model_name: diffusionModel,
      source_component: 'Mai25FlashControlPanel',
    });
  }, [diffusionModel, setModelParams, kernel]);

  const handlePromptChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    updateParam('prompt', e.target.value);
  }, [updateParam]);

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe the image or edit to create..."
          description="Image instruction. MAI 2.5 Flash requires a source; Pro and 2.6 also generate from text."
          name="prompt"
          value={params.prompt}
          onChange={handlePromptChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>
    </>
  );
};

export default memo(Mai25FlashControlPanel);
